import React, { useState, useEffect, useRef } from 'react';
import { Box, Tooltip, Typography, Switch, FormControlLabel } from '@mui/material';

/**
 * Image-Based Floor Plan with Heatmap Visualization
 * Uses the provided makerspace floor plan image with precisely positioned machine hotspots
 * and heatmap overlays based on user visits/machine usage
 * 
 * Floor Plan Dimensions: 40' x 50' (width x height)
 * Image coordinates are percentage-based (0-100%)
 */

// ============================================================================
// MACHINE POSITION MAPPING - Calibrated to match the provided floor plan image
// All coordinates are percentages (0-100) relative to image dimensions
// ============================================================================
export const FLOOR_PLAN_MACHINES = {
    // === MACHINE SHOP (Top Area - approx 0-28% height) ===
    MATERIAL_STORAGE: {
        id: 617,
        name: 'Material Storage Racks',
        x: 12,
        y: 5,
        width: 14,
        height: 8,
        zone: 'MACHINE_SHOP',
        type: 'storage'
    },
    CNC_ROUTER: {
        id: 618,
        name: 'CNC Router',
        x: 18,
        y: 12,
        width: 8,
        height: 7,
        zone: 'MACHINE_SHOP',
        type: 'machine'
    },
    LASER_CUTTER: {
        id: 619,
        name: 'Laser Cutter',
        x: 32,
        y: 8,
        width: 8,
        height: 6,
        zone: 'MACHINE_SHOP',
        type: 'machine'
    },
    LASER_VENT: {
        id: 620,
        name: 'Laser Ventilation',
        x: 42,
        y: 6,
        width: 4,
        height: 4,
        zone: 'MACHINE_SHOP',
        type: 'equipment'
    },
    DRILL_PRESS: {
        id: 621,
        name: 'Drill Press',
        x: 52,
        y: 7,
        width: 5,
        height: 5,
        zone: 'MACHINE_SHOP',
        type: 'machine'
    },
    BAND_SAW: {
        id: 622,
        name: 'Band Saw',
        x: 60,
        y: 7,
        width: 5,
        height: 5,
        zone: 'MACHINE_SHOP',
        type: 'machine'
    },
    BENCH_GRINDER1: {
        id: 623,
        name: 'Bench Grinder',
        x: 85,
        y: 5,
        width: 4,
        height: 4,
        zone: 'MACHINE_SHOP',
        type: 'machine'
    },
    BENCH_GRINDER2: {
        id: 624,
        name: 'Bench Grinder',
        x: 91,
        y: 8,
        width: 4,
        height: 4,
        zone: 'MACHINE_SHOP',
        type: 'machine'
    },
    SANDER: {
        id: 625,
        name: 'Sander',
        x: 91,
        y: 15,
        width: 4,
        height: 6,
        zone: 'MACHINE_SHOP',
        type: 'machine'
    },
    UTILITY_SINK: {
        id: 626,
        name: 'Utility Sink',
        x: 80,
        y: 18,
        width: 4,
        height: 4,
        zone: 'MACHINE_SHOP',
        type: 'utility'
    },
    EYE_STATION: {
        id: 627,
        name: 'Eye Station',
        x: 86,
        y: 18,
        width: 3,
        height: 4,
        zone: 'MACHINE_SHOP',
        type: 'safety'
    },
    EYE_WASH: {
        id: 628,
        name: 'Eye Wash',
        x: 91,
        y: 22,
        width: 3,
        height: 4,
        zone: 'MACHINE_SHOP',
        type: 'safety'
    },

    // === ELECTRONICS ZONE (Left Middle - approx 30-60% height, 0-25% width) ===
    COMPONENT_CABINET1: {
        id: 629,
        name: 'Component Cabinet',
        x: 12,
        y: 34,
        width: 6,
        height: 3,
        zone: 'ELECTRONICS',
        type: 'storage'
    },
    POWER_APY: {
        id: 630,
        name: 'Power APY',
        x: 20,
        y: 34,
        width: 5,
        height: 3,
        zone: 'ELECTRONICS',
        type: 'equipment'
    },
    ESD_MAT: {
        id: 631,
        name: 'ESD Mat',
        x: 6,
        y: 40,
        width: 4,
        height: 3,
        zone: 'ELECTRONICS',
        type: 'equipment'
    },
    SOLDERING_STATION: {
        id: 632,
        name: 'Soldering Station',
        x: 18,
        y: 40,
        width: 6,
        height: 3,
        zone: 'ELECTRONICS',
        type: 'machine'
    },
    OSCILLOSCOPES: {
        id: 633,
        name: 'Oscilloscopes',
        x: 6,
        y: 46,
        width: 8,
        height: 4,
        zone: 'ELECTRONICS',
        type: 'equipment'
    },
    POWER_SUPPLIES: {
        id: 635,
        name: 'Power Supplies',
        x: 6,
        y: 52,
        width: 5,
        height: 4,
        zone: 'ELECTRONICS',
        type: 'equipment'
    },
    COMPONENT_CABINET2: {
        id: 636,
        name: 'Component Cabinet',
        x: 6,
        y: 58,
        width: 8,
        height: 3,
        zone: 'ELECTRONICS',
        type: 'storage'
    },

    // === HEAVY DUTY WORKBENCHES (Center - approx 30-60% height, 30-70% width) ===
    WORKBENCH_1: {
        id: 642,
        name: 'Workbench 1',
        x: 38,
        y: 38,
        width: 8,
        height: 6,
        zone: 'WORKBENCH',
        type: 'workstation'
    },
    WORKBENCH_2: {
        id: 643,
        name: 'Workbench 2',
        x: 50,
        y: 38,
        width: 8,
        height: 6,
        zone: 'WORKBENCH',
        type: 'workstation'
    },
    WORKBENCH_3: {
        id: 644,
        name: 'Workbench 3',
        x: 62,
        y: 38,
        width: 8,
        height: 6,
        zone: 'WORKBENCH',
        type: 'workstation'
    },
    WORKBENCH_4: {
        id: 645,
        name: 'Workbench 4',
        x: 38,
        y: 48,
        width: 8,
        height: 6,
        zone: 'WORKBENCH',
        type: 'workstation'
    },
    WORKBENCH_5: {
        id: 646,
        name: 'Workbench 5',
        x: 50,
        y: 48,
        width: 8,
        height: 6,
        zone: 'WORKBENCH',
        type: 'workstation'
    },
    WORKBENCH_6: {
        id: 647,
        name: 'Workbench 6',
        x: 62,
        y: 48,
        width: 8,
        height: 6,
        zone: 'WORKBENCH',
        type: 'workstation'
    },

    // === 3D PRINTING ZONE (Right Middle - approx 30-60% height, 75-98% width) ===
    PRINTER_1: {
        id: 637,
        name: '3D Printer 1',
        x: 84,
        y: 36,
        width: 5,
        height: 5,
        zone: 'PRINTING_3D',
        type: 'machine'
    },
    PRINTER_2: {
        id: 638,
        name: '3D Printer 2',
        x: 91,
        y: 36,
        width: 5,
        height: 5,
        zone: 'PRINTING_3D',
        type: 'machine'
    },
    PRINTER_3: {
        id: 639,
        name: '3D Printer 3',
        x: 84,
        y: 44,
        width: 5,
        height: 5,
        zone: 'PRINTING_3D',
        type: 'machine'
    },
    PRINTER_4: {
        id: 640,
        name: '3D Printer 4',
        x: 91,
        y: 44,
        width: 5,
        height: 5,
        zone: 'PRINTING_3D',
        type: 'machine'
    },
    FILAMENT_STORAGE: {
        id: 641,
        name: 'Filament Storage',
        x: 94,
        y: 52,
        width: 4,
        height: 6,
        zone: 'PRINTING_3D',
        type: 'storage'
    },

    // === BOTTOM ROW: Storage and Support ===
    TOOL_CABINET_1: {
        id: 648,
        name: 'Tool Cabinet',
        x: 32,
        y: 62,
        width: 6,
        height: 3,
        zone: 'WORKBENCH',
        type: 'storage'
    },
    TOOL_CABINET_2: {
        id: 649,
        name: 'Tool Cabinet',
        x: 42,
        y: 62,
        width: 6,
        height: 3,
        zone: 'WORKBENCH',
        type: 'storage'
    },

    // === CONFERENCE ROOM (Bottom Right) ===
    CONFERENCE_TABLE: {
        id: 650,
        name: 'Conference Table',
        x: 78,
        y: 82,
        width: 16,
        height: 10,
        zone: 'CONFERENCE',
        type: 'furniture'
    },
    AV_SCREEN: {
        id: 651,
        name: 'AV Screen',
        x: 92,
        y: 94,
        width: 5,
        height: 2,
        zone: 'CONFERENCE',
        type: 'equipment'
    }
};

// Zone boundaries (percentage-based)
export const FLOOR_PLAN_ZONE_BOUNDS = {
    MACHINE_SHOP: { minX: 2, maxX: 98, minY: 2, maxY: 28, color: '#d4eef7', label: 'Machine Shop' },
    ELECTRONICS: { minX: 2, maxX: 28, minY: 30, maxY: 62, color: '#fff8dc', label: 'Electronics Zone' },
    WORKBENCH: { minX: 30, maxX: 72, minY: 30, maxY: 62, color: '#f5f5f5', label: 'Workbench Area' },
    PRINTING_3D: { minX: 74, maxX: 98, minY: 30, maxY: 62, color: '#e8f5e9', label: '3D Printing Zone' },
    STORAGE_ROW: { minX: 2, maxX: 98, minY: 62, maxY: 70, color: '#eceff1', label: 'Storage & Cabinets' },
    SMALL_OFFICE: { minX: 2, maxX: 18, minY: 72, maxY: 98, color: '#e3f2fd', label: 'Small Office' },
    ADMIN: { minX: 20, maxX: 48, minY: 72, maxY: 98, color: '#e8f5e9', label: 'Admin/Reception' },
    CONFERENCE: { minX: 62, maxX: 98, minY: 72, maxY: 98, color: '#fff3e0', label: 'Conference Room' },
    ENTRANCE: { minX: 40, maxX: 60, minY: 95, maxY: 100, color: '#fafafa', label: 'Main Entrance' }
};

// Heatmap color scale (cold to hot)
const getHeatColor = (intensity, alpha = 0.6) => {
    // intensity: 0-1
    if (intensity <= 0) return `rgba(76, 175, 80, ${alpha * 0.3})`; // Light green (cold)
    if (intensity <= 0.2) return `rgba(139, 195, 74, ${alpha})`; // Green
    if (intensity <= 0.4) return `rgba(255, 235, 59, ${alpha})`; // Yellow
    if (intensity <= 0.6) return `rgba(255, 193, 7, ${alpha})`; // Amber
    if (intensity <= 0.8) return `rgba(255, 152, 0, ${alpha})`; // Orange
    return `rgba(244, 67, 54, ${alpha})`; // Red (hot)
};

const ImageFloorPlanHeatmap = ({
    liveUsers = [],
    liveMachines = [],
    machineVisits = {}, // { machineId: visitCount }
    showHeatmap = true,
    showMachines = true,
    showUsers = true,
    showZoneLabels = true,
    onMachineClick,
    onZoneClick,
    floorPlanImage = '/makerspace_floorplan.png', // Will use floor_plan.png as fallback
}) => {
    const containerRef = useRef(null);
    const [hoveredMachine, setHoveredMachine] = useState(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

    // Track container size for responsive positioning
    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                setContainerSize({
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight
                });
            }
        };

        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    // Calculate heatmap intensity for each machine based on visits
    const getVisitIntensity = (machineId) => {
        const visits = machineVisits[machineId] || 0;
        const maxVisits = Math.max(...Object.values(machineVisits), 10); // min 10 to avoid division issues
        return Math.min(visits / maxVisits, 1);
    };

    // Get machine status from live data
    const getMachineStatus = (machineId) => {
        const machine = liveMachines.find(m => m.id === machineId);
        return {
            isActive: machine?.status === 'active',
            load: machine?.load || 0,
            currentUser: machine?.current_user || null
        };
    };

    // Calculate zone heatmap based on users in zone
    const getZoneHeatIntensity = (zoneBounds) => {
        if (!liveUsers.length) return 0;
        const usersInZone = liveUsers.filter(user => {
            const x = parseFloat(user.x);
            const y = parseFloat(user.y);
            return x >= zoneBounds.minX && x <= zoneBounds.maxX &&
                   y >= zoneBounds.minY && y <= zoneBounds.maxY;
        }).length;
        return Math.min(usersInZone / 5, 1); // Max 5 users = full heat
    };

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '800px',
                margin: '0 auto',
                bgcolor: '#f8f9fa',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
            }}
        >
            {/* Floor Plan Image */}
            <img
                src={floorPlanImage}
                alt="Makerspace Floor Plan"
                style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                }}
                onError={(e) => {
                    // Fallback to existing floor plan if new one doesn't exist
                    e.target.src = '/floor_plan.png';
                }}
            />

            {/* Zone Heatmap Overlay */}
            {showHeatmap && Object.entries(FLOOR_PLAN_ZONE_BOUNDS).map(([zoneKey, zone]) => {
                const intensity = getZoneHeatIntensity(zone);
                if (intensity <= 0) return null;

                return (
                    <Box
                        key={`zone-heat-${zoneKey}`}
                        onClick={() => onZoneClick?.(zoneKey, zone)}
                        sx={{
                            position: 'absolute',
                            left: `${zone.minX}%`,
                            top: `${zone.minY}%`,
                            width: `${zone.maxX - zone.minX}%`,
                            height: `${zone.maxY - zone.minY}%`,
                            backgroundColor: getHeatColor(intensity, 0.3),
                            pointerEvents: 'none',
                            transition: 'background-color 0.5s ease',
                        }}
                    />
                );
            })}

            {/* Machine Heatmap Hotspots */}
            {showHeatmap && showMachines && Object.entries(FLOOR_PLAN_MACHINES).map(([machineKey, machine]) => {
                const visitIntensity = getVisitIntensity(machine.id);
                const status = getMachineStatus(machine.id);
                
                // Skip machines with no visits and not active
                if (visitIntensity <= 0 && !status.isActive) return null;

                // Use higher intensity if machine is active
                const displayIntensity = status.isActive 
                    ? Math.max(visitIntensity, 0.5 + (status.load / 200))
                    : visitIntensity;

                const heatSize = Math.max(8, displayIntensity * 25); // 8-25% size based on intensity

                return (
                    <Tooltip
                        key={`heat-${machineKey}`}
                        title={
                            <Box sx={{ p: 0.5 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>
                                    {machine.name}
                                </Typography>
                                <Typography sx={{ fontSize: '11px', color: '#ddd' }}>
                                    Visits: {machineVisits[machine.id] || 0}
                                </Typography>
                                {status.isActive && (
                                    <Typography sx={{ fontSize: '11px', color: '#4caf50' }}>
                                        Active • {status.load}% Load
                                    </Typography>
                                )}
                            </Box>
                        }
                        arrow
                        placement="top"
                    >
                        <Box
                            onClick={() => onMachineClick?.(machineKey, machine)}
                            onMouseEnter={() => setHoveredMachine(machineKey)}
                            onMouseLeave={() => setHoveredMachine(null)}
                            sx={{
                                position: 'absolute',
                                left: `${machine.x}%`,
                                top: `${machine.y}%`,
                                width: `${heatSize}%`,
                                height: `${heatSize * 0.8}%`,
                                transform: 'translate(-50%, -50%)',
                                background: `radial-gradient(ellipse at center, ${getHeatColor(displayIntensity, 0.8)} 0%, ${getHeatColor(displayIntensity, 0.2)} 60%, transparent 100%)`,
                                borderRadius: '50%',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                zIndex: hoveredMachine === machineKey ? 50 : 10,
                                filter: hoveredMachine === machineKey ? 'brightness(1.2)' : 'none',
                                animation: status.isActive ? 'pulse 2s infinite' : 'none',
                                '@keyframes pulse': {
                                    '0%, 100%': { opacity: 0.8, transform: 'translate(-50%, -50%) scale(1)' },
                                    '50%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1.1)' },
                                },
                            }}
                        />
                    </Tooltip>
                );
            })}

            {/* Machine Position Markers (when no heatmap data) */}
            {showMachines && Object.entries(FLOOR_PLAN_MACHINES).map(([machineKey, machine]) => {
                const status = getMachineStatus(machine.id);
                const visitIntensity = getVisitIntensity(machine.id);
                
                // Skip if already showing as heatmap
                if (showHeatmap && (visitIntensity > 0 || status.isActive)) return null;

                return (
                    <Tooltip
                        key={`marker-${machineKey}`}
                        title={machine.name}
                        arrow
                        placement="top"
                    >
                        <Box
                            onClick={() => onMachineClick?.(machineKey, machine)}
                            sx={{
                                position: 'absolute',
                                left: `${machine.x}%`,
                                top: `${machine.y}%`,
                                width: '12px',
                                height: '12px',
                                transform: 'translate(-50%, -50%)',
                                backgroundColor: status.isActive ? '#4caf50' : '#9e9e9e',
                                border: '2px solid #fff',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    transform: 'translate(-50%, -50%) scale(1.3)',
                                    zIndex: 100,
                                },
                            }}
                        />
                    </Tooltip>
                );
            })}

            {/* Live User Positions */}
            {showUsers && liveUsers.map((user, index) => (
                <Tooltip
                    key={user.id || `user-${index}`}
                    title={
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '12px' }}>
                                {user.name || `User ${user.id}`}
                            </Typography>
                            {user.current_machine && (
                                <Typography sx={{ fontSize: '10px', color: '#ddd' }}>
                                    Using: {user.current_machine}
                                </Typography>
                            )}
                        </Box>
                    }
                    arrow
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            left: `${user.x}%`,
                            top: `${user.y}%`,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            transform: 'translate(-50%, -50%)',
                            transition: 'all 1.5s linear',
                            zIndex: 100,
                        }}
                    >
                        {/* User Dot with Pulse */}
                        <Box
                            sx={{
                                width: '14px',
                                height: '14px',
                                bgcolor: '#2196f3',
                                border: '2px solid #fff',
                                borderRadius: '50%',
                                boxShadow: '0 2px 8px rgba(33, 150, 243, 0.5)',
                                animation: 'userPulse 2s infinite',
                                '@keyframes userPulse': {
                                    '0%, 100%': { boxShadow: '0 2px 8px rgba(33, 150, 243, 0.5)' },
                                    '50%': { boxShadow: '0 2px 16px rgba(33, 150, 243, 0.8)' },
                                },
                            }}
                        />
                        {/* User Name Label */}
                        <Typography
                            sx={{
                                mt: 0.3,
                                fontSize: '8px',
                                fontWeight: 700,
                                color: '#333',
                                bgcolor: 'rgba(255, 255, 255, 0.95)',
                                px: 0.6,
                                py: 0.15,
                                borderRadius: '8px',
                                border: '1px solid #e0e0e0',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                whiteSpace: 'nowrap',
                                maxWidth: '60px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {user.name || `U${user.id}`}
                        </Typography>
                    </Box>
                </Tooltip>
            ))}

            {/* Zone Labels */}
            {showZoneLabels && Object.entries(FLOOR_PLAN_ZONE_BOUNDS).map(([zoneKey, zone]) => {
                // Only show labels for main zones
                if (['STORAGE_ROW', 'ENTRANCE'].includes(zoneKey)) return null;
                
                return (
                    <Typography
                        key={`label-${zoneKey}`}
                        sx={{
                            position: 'absolute',
                            left: `${(zone.minX + zone.maxX) / 2}%`,
                            top: `${(zone.minY + zone.maxY) / 2}%`,
                            transform: 'translate(-50%, -50%)',
                            fontSize: '9px',
                            fontWeight: 700,
                            color: 'rgba(0,0,0,0.4)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            pointerEvents: 'none',
                            textShadow: '0 1px 2px rgba(255,255,255,0.8)',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {zone.label}
                    </Typography>
                );
            })}

            {/* Legend */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 8,
                    left: 8,
                    display: 'flex',
                    gap: 1.5,
                    bgcolor: 'rgba(255,255,255,0.95)',
                    px: 1.5,
                    py: 0.8,
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    fontSize: '10px',
                    fontWeight: 500,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2196f3' }} />
                    <span>User</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: '#4caf50' }} />
                    <span>Active</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: '#9e9e9e' }} />
                    <span>Idle</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ 
                        width: 14, 
                        height: 10, 
                        background: 'linear-gradient(90deg, #4caf50, #ffeb3b, #ff9800, #f44336)',
                        borderRadius: '2px'
                    }} />
                    <span>Heat</span>
                </Box>
            </Box>
        </Box>
    );
};

export default ImageFloorPlanHeatmap;
